import React from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { FilterSelect } from '../../common/FilterSelect/FilterSelect';
import { Author } from '../../common/Rest/Services/AuthorsService';
import { Genre } from '../../common/Rest/Services/GenresService';
import { Publisher } from '../../common/Rest/Services/PublishersService';
import { AvailabilityFilter, Filters, hasActiveFilters } from './HomePage';

interface FilterPanelProps {
  filters: Filters;
  authors: Author[];
  genres: Genre[];
  publishers: Publisher[];
  fetchAuthors: (search: string) => Promise<Author[]>;
  fetchGenres: (search: string) => Promise<Genre[]>;
  fetchPublishers: (search: string) => Promise<Publisher[]>;
  onAuthorChange: (author: Author) => void;
  onGenreChange: (genre: Genre) => void;
  onPublisherChange: (publisher: Publisher) => void;
  onAvailabilityChange: (availability: AvailabilityFilter) => void;
  onResetFilters: () => void;
  onApply: () => void;
  hasPendingChanges: boolean;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  authors,
  genres,
  publishers,
  fetchAuthors,
  fetchGenres,
  fetchPublishers,
  onAuthorChange,
  onGenreChange,
  onPublisherChange,
  onAvailabilityChange,
  onResetFilters,
  onApply,
  hasPendingChanges,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box
        sx={{
          flexShrink: 0,
          p: 2,
          pb: 1.5,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.06)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Фільтри
          </Typography>
          {hasActiveFilters(filters) && (
            <Button size="small" onClick={onResetFilters}>
              Скинути фільтри
            </Button>
          )}
        </Box>
        {hasPendingChanges && (
          <Button variant="contained" fullWidth onClick={onApply}>
            Застосувати фільтри
          </Button>
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, p: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Наявність
          </Typography>
          <ToggleButtonGroup
            value={filters.availability}
            exclusive
            onChange={(_, value: AvailabilityFilter | null) => {
              if (value) onAvailabilityChange(value);
            }}
            size="small"
            fullWidth
          >
            <ToggleButton value="all">Усі</ToggleButton>
            <ToggleButton value="in">Є</ToggleButton>
            <ToggleButton value="out">Немає</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <FilterSelect<Author>
          title="Автори"
          selected={filters.authors}
          selectedItems={authors.filter((author) => filters.authors.includes(author.id))}
          fetchItems={fetchAuthors}
          onChange={onAuthorChange}
          getLabel={(author) => author.name}
          searchPlaceholder="Пошук авторів..."
        />
        <FilterSelect<Genre>
          title="Жанри"
          selected={filters.genres}
          selectedItems={genres.filter((genre) => filters.genres.includes(genre.id))}
          fetchItems={fetchGenres}
          onChange={onGenreChange}
          getLabel={(genre) => genre.name}
          searchPlaceholder="Пошук жанрів..."
        />
        <FilterSelect<Publisher>
          title="Видавництва"
          selected={filters.publishers}
          selectedItems={publishers.filter((publisher) => filters.publishers.includes(publisher.id))}
          fetchItems={fetchPublishers}
          onChange={onPublisherChange}
          getLabel={(publisher) => publisher.name}
          searchPlaceholder="Пошук видавництв..."
        />
      </Box>
    </Box>
  );
};

export default FilterPanel;
